import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { atsFetch, fmtMoney, fmtDate, AtsTask, AtsActivity, AtsSuggestion, AtsKpis, KPI_LABELS } from './api';
import { ActivityTimeline, SectionCard, SuggestionCard, Modal, Field, inputCls, btnPrimary, btnGhost } from './shared';

interface Overview {
  clients: number;
  rolesByStatus: { status: string; n: number }[];
  pipeline: { stage_id: number; name: string; color: string | null; n: number }[];
  placementsThisQuarter: { n: number; fees: string };
  openTasks: AtsTask[];
  recentActivity: AtsActivity[];
  suggestions: AtsSuggestion[];
}

const PERIODS = [
  { key: 'week', label: 'This week' },
  { key: 'month', label: 'This month' },
  { key: 'quarter', label: 'This quarter' },
];

// KPI tile: value vs target (targets are monthly, scaled server-side).
const KpiTile: React.FC<{ label: string; value: number; target?: number; money?: boolean }> = ({ label, value, target, money }) => {
  const pct = target ? Math.min(100, Math.round((value / target) * 100)) : null;
  const hit = pct !== null && pct >= 100;
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-4 py-3.5">
      <p className="text-[11px] font-medium text-gray-500">{label}</p>
      <p className={`text-xl font-semibold mt-0.5 ${money ? 'text-emerald-600' : 'text-gray-900'}`}>
        {money ? fmtMoney(value) : value}
      </p>
      {target ? (
        <div className="mt-2">
          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div className={`h-full rounded-full ${hit ? 'bg-emerald-500' : 'bg-blue-500'}`} style={{ width: `${pct}%` }} />
          </div>
          <p className={`text-[10px] mt-1 ${hit ? 'text-emerald-600 font-medium' : 'text-gray-400'}`}>
            {pct}% of {money ? fmtMoney(target) : target}{hit ? ' — hit' : ''}
          </p>
        </div>
      ) : (
        <p className="text-[10px] text-gray-300 mt-2">No target set</p>
      )}
    </div>
  );
};

const AtsOverview: React.FC = () => {
  const { token } = useAuth();
  const [data, setData] = useState<Overview | null>(null);
  const [kpis, setKpis] = useState<AtsKpis | null>(null);
  const [period, setPeriod] = useState('month');
  const [showTargets, setShowTargets] = useState(false);
  const [targetForm, setTargetForm] = useState<Record<string, string>>({});
  const [savingTargets, setSavingTargets] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) return;
    atsFetch(token, '/overview').then(setData).catch((e) => setError(e.message));
  }, [token]);

  useEffect(() => {
    if (!token) return;
    atsFetch(token, `/kpis?period=${period}`).then(setKpis).catch(() => {});
  }, [token, period]);

  const openTargets = () => {
    if (!token) return;
    // Show monthly values (what's stored), not the period-scaled ones.
    atsFetch(token, '/kpis?period=month').then((d: AtsKpis) => {
      setTargetForm(Object.fromEntries(KPI_LABELS.map((k) => [k.key, d.targets[k.key] !== undefined ? String(d.targets[k.key]) : ''])));
      setShowTargets(true);
    }).catch(() => {});
  };

  const saveTargets = async () => {
    if (!token) return;
    setSavingTargets(true);
    try {
      const targets: Record<string, number | null> = {};
      for (const k of KPI_LABELS) targets[k.key] = targetForm[k.key] === '' ? null : Number(targetForm[k.key]);
      await atsFetch(token, '/targets', { method: 'PUT', body: JSON.stringify({ targets }) });
      setShowTargets(false);
      atsFetch(token, `/kpis?period=${period}`).then(setKpis).catch(() => {});
    } catch (e: any) { setError(e.message); }
    setSavingTargets(false);
  };

  if (error) return <p className="text-sm text-red-600">{error}</p>;
  if (!data) return <div className="py-16 text-center text-gray-400 text-sm">Loading…</div>;

  const openRoles = data.rolesByStatus.filter((r) => ['lead', 'open', 'interviewing'].includes(r.status)).reduce((s, r) => s + r.n, 0);
  const inPipeline = data.pipeline.reduce((s, p) => s + p.n, 0);
  const maxStage = Math.max(1, ...data.pipeline.map((p) => p.n));

  const tiles = [
    { label: 'Clients', value: data.clients, to: '/ats/clients' },
    { label: 'Open roles', value: openRoles, to: '/ats/roles' },
    { label: 'In pipelines', value: inPipeline, to: '/ats/roles' },
    { label: 'Placements this quarter', value: data.placementsThisQuarter.n, sub: fmtMoney(data.placementsThisQuarter.fees), to: '/ats/placements' },
  ];

  return (
    <div className="space-y-6">
      {/* KPI strip: event-sourced from stage moves + placements */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="flex gap-1">
            {PERIODS.map((p) => (
              <button
                key={p.key}
                onClick={() => setPeriod(p.key)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium ${period === p.key ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <button onClick={openTargets} className="text-xs font-medium text-emerald-700 hover:text-emerald-800">Set targets</button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {KPI_LABELS.map((k) => (
            <KpiTile
              key={k.key}
              label={k.label}
              value={kpis ? kpis.metrics[k.key] || 0 : 0}
              target={kpis ? kpis.targets[k.key] : undefined}
              money={k.money}
            />
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {tiles.map((t) => (
          <Link key={t.label} to={t.to} className="bg-white rounded-xl border border-gray-200 shadow-sm px-5 py-4 hover:border-blue-200 hover:shadow transition-all">
            <p className="text-xs font-medium text-gray-500">{t.label}</p>
            <p className="text-2xl font-semibold text-gray-900 mt-1">{t.value}</p>
            {t.sub && <p className="text-xs text-emerald-600 font-medium mt-0.5">{t.sub} booked</p>}
          </Link>
        ))}
      </div>

      {(data.suggestions || []).length > 0 && (
        <SectionCard title="Suggested follow-ups">
          <div className="grid lg:grid-cols-2 gap-x-6">
            {data.suggestions.map((s) => (
              <SuggestionCard
                key={s.id}
                s={s}
                onDone={(id) => setData((d) => (d ? { ...d, suggestions: d.suggestions.filter((x) => x.id !== id) } : d))}
              />
            ))}
          </div>
        </SectionCard>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        <SectionCard title="Pipeline by stage">
          {data.pipeline.length === 0 ? (
            <p className="text-sm text-gray-400">No one in a pipeline yet — open a role and add candidates.</p>
          ) : (
            <div className="space-y-2.5">
              {data.pipeline.map((p) => (
                <div key={p.stage_id} className="flex items-center gap-3">
                  <span className="w-24 text-xs text-gray-500 truncate">{p.name}</span>
                  <div className="flex-1 bg-gray-100 rounded-full h-5 overflow-hidden">
                    <div className="h-full rounded-full flex items-center px-2" style={{ width: `${Math.max(7, (p.n / maxStage) * 100)}%`, backgroundColor: p.color || '#3B82F6' }}>
                      <span className="text-[10px] font-semibold text-white">{p.n}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </SectionCard>

        <SectionCard title="Tasks due" action={<Link to="/ats/tasks" className="text-xs font-medium text-blue-600 hover:text-blue-700">All tasks</Link>}>
          {data.openTasks.length === 0 ? (
            <p className="text-sm text-gray-400">Nothing due. Quiet desk or missing follow-ups?</p>
          ) : (
            <ul className="space-y-2">
              {data.openTasks.map((t) => {
                const overdue = t.due_at && new Date(t.due_at).getTime() < Date.now();
                return (
                  <li key={t.id} className="flex items-center gap-2 text-sm">
                    <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${overdue ? 'bg-red-500' : 'bg-gray-300'}`} />
                    <span className="text-gray-700 flex-1 truncate">{t.title}</span>
                    {t.due_at && <span className={`text-[11px] ${overdue ? 'text-red-500 font-medium' : 'text-gray-400'}`}>{fmtDate(t.due_at)}</span>}
                  </li>
                );
              })}
            </ul>
          )}
        </SectionCard>
      </div>

      <SectionCard title="Recent activity">
        <ActivityTimeline activities={data.recentActivity} />
      </SectionCard>

      {showTargets && (
        <Modal title="Monthly targets" onClose={() => setShowTargets(false)}>
          <p className="text-xs text-gray-400 mb-4">Stored per month; the dashboard scales them to the selected period (week ≈ ×0.23, quarter ×3). Leave blank for no target.</p>
          <div className="grid grid-cols-2 gap-3">
            {KPI_LABELS.map((k) => (
              <Field key={k.key} label={k.label + (k.money ? ' (£)' : '')}>
                <input
                  type="number"
                  value={targetForm[k.key] || ''}
                  onChange={(e) => setTargetForm({ ...targetForm, [k.key]: e.target.value })}
                  className={inputCls}
                />
              </Field>
            ))}
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <button onClick={() => setShowTargets(false)} className={btnGhost}>Cancel</button>
            <button onClick={saveTargets} disabled={savingTargets} className={btnPrimary}>{savingTargets ? 'Saving…' : 'Save targets'}</button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default AtsOverview;
